import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Palette, Loader2, Save, UploadCloud } from 'lucide-react';
import { useBrand, applyAccent, DEFAULT_BRAND } from '../context/BrandContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PRESETS = ['#00AEEF', '#2563EB', '#7C3AED', '#0F766E', '#DB2777', '#EA580C', '#16A34A', '#0E3A4D'];
const ACCEPT = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml'];

export const BrandSettingsCard = ({ isAdmin }) => {
  const { brand, setBrand, refreshBrand } = useBrand();
  const [form, setForm] = useState(brand);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { setForm(brand); }, [brand]);

  const set = (k, v) => {
    const next = { ...form, [k]: v };
    setForm(next);
    if (k === 'accent_color') applyAccent(v); // live preview
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    if (!ACCEPT.includes(file.type)) { toast.error('Use a PNG, JPG, GIF, WEBP or SVG image'); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error('Logo must be under 2MB'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/brand-settings/logo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const b = { ...DEFAULT_BRAND, ...res.data };
      setBrand(b);
      setForm(b);
      toast.success('Logo uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Logo upload failed');
    } finally { setUploading(false); }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    if (!isAdmin) return;
    const f = e.dataTransfer?.files?.[0];
    if (f) uploadLogo(f);
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/brand-settings`, {
        brand_name: form.brand_name,
        logo_url: form.logo_url,
        accent_color: form.accent_color,
        sender_name: form.sender_name,
      });
      const b = { ...DEFAULT_BRAND, ...res.data };
      setBrand(b);
      applyAccent(b.accent_color);
      toast.success('Brand settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save brand settings');
      refreshBrand();
    } finally { setSaving(false); }
  };

  return (
    <Card className="bg-slate-900/50 border-slate-800" data-testid="brand-settings-card">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-violet-500/10 rounded-lg">
            <Palette className="w-5 h-5 text-violet-400" />
          </div>
          <div>
            <CardTitle className="text-lg text-white">Brand</CardTitle>
            <CardDescription>Your app name, logo, accent colour and default sender - applied across the app and outreach emails.</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-slate-300 text-sm">Brand name</Label>
            <Input value={form.brand_name || ''} onChange={(e) => set('brand_name', e.target.value)} disabled={!isAdmin}
              className="bg-slate-950 border-slate-800 mt-1" data-testid="brand-name-input" placeholder="Care Tech Compare" />
          </div>
          <div>
            <Label className="text-slate-300 text-sm">Default sender name</Label>
            <Input value={form.sender_name || ''} onChange={(e) => set('sender_name', e.target.value)} disabled={!isAdmin}
              className="bg-slate-950 border-slate-800 mt-1" data-testid="brand-sender-input" placeholder="Care Tech Compare" />
          </div>
        </div>

        <div>
          <Label className="text-slate-300 text-sm">Logo</Label>
          <div className="flex items-center gap-4 mt-1.5">
            <div className="w-16 h-16 rounded-lg bg-white border border-slate-800 flex items-center justify-center shrink-0 overflow-hidden">
              {form.logo_url ? <img src={form.logo_url} alt="logo" className="w-14 h-14 object-contain" /> : null}
            </div>
            <div
              onDragOver={(e) => { e.preventDefault(); if (isAdmin) setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              onClick={() => isAdmin && fileRef.current?.click()}
              data-testid="brand-logo-dropzone"
              className={`flex-1 cursor-pointer rounded-lg border-2 border-dashed px-4 py-5 text-center transition-colors ${dragOver ? 'border-violet-500 bg-violet-500/10' : 'border-slate-700 hover:border-violet-600/60'} ${!isAdmin ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              {uploading ? (
                <span className="text-sm text-slate-300 flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</span>
              ) : (
                <span className="text-sm text-slate-400 flex items-center justify-center gap-2">
                  <UploadCloud className="w-4 h-4" /> Drag & drop a logo, or <span className="text-violet-400 underline">browse</span>
                </span>
              )}
              <p className="text-[11px] text-slate-500 mt-1">PNG, JPG, GIF, WEBP or SVG · max 2MB</p>
              <input ref={fileRef} type="file" accept={ACCEPT.join(',')} className="hidden"
                data-testid="brand-logo-file-input"
                onChange={(e) => { uploadLogo(e.target.files?.[0]); e.target.value = ''; }} />
            </div>
          </div>
          <Input value={form.logo_url || ''} onChange={(e) => set('logo_url', e.target.value)} disabled={!isAdmin}
            className="bg-slate-950 border-slate-800 mt-2 text-xs" data-testid="brand-logo-input" placeholder="…or paste a logo URL" />
        </div>

        <div>
          <Label className="text-slate-300 text-sm">Accent colour</Label>
          <div className="flex items-center gap-3 mt-2">
            <input type="color" value={form.accent_color || '#00AEEF'} onChange={(e) => set('accent_color', e.target.value)} disabled={!isAdmin}
              className="w-10 h-10 rounded-md border border-slate-700 bg-transparent cursor-pointer" data-testid="brand-accent-picker" />
            <Input value={form.accent_color || ''} onChange={(e) => set('accent_color', e.target.value)} disabled={!isAdmin}
              className="bg-slate-950 border-slate-800 w-32 font-mono uppercase" data-testid="brand-accent-input" maxLength={7} />
            <div className="flex items-center gap-1.5">
              {PRESETS.map(c => (
                <button key={c} type="button" disabled={!isAdmin} onClick={() => set('accent_color', c)} title={c}
                  className="w-6 h-6 rounded-full border border-slate-700 transition-transform hover:scale-110"
                  style={{ backgroundColor: c }} data-testid={`brand-preset-${c.replace('#', '')}`} />
              ))}
            </div>
          </div>
        </div>

        {isAdmin ? (
          <Button onClick={save} disabled={saving} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="brand-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save brand
          </Button>
        ) : (
          <p className="text-xs text-slate-500">Only workspace admins can change brand settings.</p>
        )}
      </CardContent>
    </Card>
  );
};
